import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material';

const MATRIX_CHARS = '01<>[]{}#@$%&*!()&/~;:"=+-_?^±§°|\\,.`';
const FONT_SIZE = 22;
const SPEED = 60; // ms per frame

const AnimatedHeroBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const dropsRef = useRef<number[]>([]);
  const columnsRef = useRef<number>(0);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight; // Use full viewport height
    canvas.width = width;
    canvas.height = height;

    let columns = Math.floor(width / FONT_SIZE);
    columnsRef.current = columns;
    dropsRef.current = Array(columns).fill(1);

    function draw() {
      if (!canvasRef.current) return;
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;
      // Only change background and rain color in light mode
      if (theme.palette.mode === 'light') {
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.fillRect(0, 0, width, height);
        ctx.font = `${FONT_SIZE}px monospace`;
        ctx.fillStyle = theme.palette.primary.main;
        for (let i = 0; i < columnsRef.current; i++) {
          const text = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
          ctx.globalAlpha = 0.7;
          ctx.fillText(text, i * FONT_SIZE, dropsRef.current[i] * FONT_SIZE);
          if (dropsRef.current[i] * FONT_SIZE > height && Math.random() > 0.975) {
            dropsRef.current[i] = 0;
          }
          dropsRef.current[i]++;
        }
        ctx.globalAlpha = 0.1;
      } else {
        ctx.fillStyle = 'rgba(10,25,47,0.3)';
        ctx.fillRect(0, 0, width, height);
        ctx.font = `${FONT_SIZE}px monospace`;
        ctx.fillStyle = '#64ffda';
        for (let i = 0; i < columnsRef.current; i++) {
          const text = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
          ctx.globalAlpha = 0.7;
          ctx.fillText(text, i * FONT_SIZE, dropsRef.current[i] * FONT_SIZE);
          if (dropsRef.current[i] * FONT_SIZE > height && Math.random() > 0.975) {
            dropsRef.current[i] = 0;
          }
          dropsRef.current[i]++;
        }
        ctx.globalAlpha = 0.25;
      }
      animationRef.current = window.setTimeout(draw, SPEED);
    }

    draw();
    function handleResize() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      columnsRef.current = Math.floor(width / FONT_SIZE);
      dropsRef.current = Array(columnsRef.current).fill(1);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) clearTimeout(animationRef.current);
    };
  }, [theme.palette.mode, theme.palette.primary.main]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.25,
      }}
      width={window.innerWidth}
      height={window.innerHeight}
      aria-hidden="true"
    />
  );
};

export default AnimatedHeroBackground;
